/**
 * Scroll-based reveal animations
 * Uses IntersectionObserver for performant entrance effects
 */
import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Hook: returns [ref, isVisible]
 */
export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}

/**
 * ScrollReveal wrapper — animates children on viewport entry
 *
 * Props:
 *   direction: 'up' | 'down' | 'left' | 'right' | 'fade'
 *   delay: ms delay (for staggering)
 *   distance: px to travel (default 40)
 *   duration: seconds (default 0.7)
 *   sx: extra sx passed to wrapper
 */
export function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  distance = 40,
  duration = 0.7,
  sx = {},
  ...props
}) {
  const [ref, isVisible] = useScrollReveal();

  const transforms = {
    up: `translateY(${distance}px)`,
    down: `translateY(-${distance}px)`,
    left: `translateX(${distance}px)`,
    right: `translateX(-${distance}px)`,
    fade: 'none',
  };

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'none' : transforms[direction],
        transition: `opacity ${duration}s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform ${duration}s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
        willChange: 'opacity, transform',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

/**
 * StaggerChildren — each direct child animates with increasing delay
 */
export function StaggerReveal({
  children,
  direction = 'up',
  staggerDelay = 0.1,
  baseDelay = 0,
  distance = 30,
  duration = 0.6,
  sx = {},
  ...props
}) {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Box ref={ref} sx={sx} {...props}>
      {React.Children.map(children, (child, index) => (
        <Box
          sx={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible
              ? 'none'
              : direction === 'up'
                ? `translateY(${distance}px)`
                : direction === 'left'
                  ? `translateX(${distance}px)`
                  : `translateY(${distance}px)`,
            transition: `opacity ${duration}s cubic-bezier(0.16, 1, 0.3, 1) ${baseDelay + index * staggerDelay}s, transform ${duration}s cubic-bezier(0.16, 1, 0.3, 1) ${baseDelay + index * staggerDelay}s`,
            willChange: 'opacity, transform',
          }}
        >
          {child}
        </Box>
      ))}
    </Box>
  );
}
